import * as React from 'react';
import { Alert, Empty, message, Select, Spin, Typography } from 'antd';
import type { SelectProps } from 'antd';
import dayjs from 'dayjs';
import { Country, Centre, School } from 'app/shared/model';
import { HolidayRecord } from 'app/shared/model/Holiday';
import { HolidayRow, HolidayRowValidation } from '../types';
import { formatSchoolEntityId, normalizeSchoolSelection } from '../utils';
import {
    getAllCentres,
    getAllCountries,
    getAllSchools,
    getHolidaysByPricingYear,
    createHolidays,
    deleteHolidays,
} from 'app/shared/api';
import SchoolSelector from './SchoolSelector';
import HolidayToolbar from './HolidayToolbar';
import HolidayTable from './HolidayTable';
import './HolidayManager.css';
import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';

const { Title, Text } = Typography;

const FUTURE_YEAR_COUNT = 3;
const PAST_YEAR_COUNT = 10;

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
    items.slice().sort((a, b) => a.name.localeCompare(b.name));

const HolidayManagerPage: React.FC = () => {
    const [countries, setCountries] = React.useState<Country[]>([]);
    const [centres, setCentres] = React.useState<Centre[]>([]);
    const [schools, setSchools] = React.useState<School[]>([]);

    const fallbackYear = React.useMemo(() => new Date().getFullYear(), []);
    const [selectedYear, setSelectedYear] = React.useState<number | undefined>(fallbackYear);
    const [selectedSchoolIds, setSelectedSchoolIds] = React.useState<string[]>([]);

    const [holidayRows, setHolidayRows] = React.useState<HolidayRow[]>([]);
    const [focusRowClientId, setFocusRowClientId] = React.useState<string | null>(null);

    const [isLoadingReference, setIsLoadingReference] = React.useState<boolean>(true);
    const [isLoadingHolidays, setIsLoadingHolidays] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [copying, setCopying] = React.useState<boolean>(false);
    const [deleting, setDeleting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const loadTokenRef = React.useRef(0);
    const uniqueIdCounterRef = React.useRef(0);

    const schoolNameMap = React.useMemo(() => {
        const map = new Map<string, string>();
        schools.forEach((school) => {
            const id = formatSchoolEntityId(school.id ?? school.code);
            if (id) {
                map.set(id, school.name);
            }
        });
        return map;
    }, [schools]);

    const availableYears = React.useMemo(() => {
        const startYear = fallbackYear - PAST_YEAR_COUNT;
        const endYear = fallbackYear + FUTURE_YEAR_COUNT;
        const years: number[] = [];
        for (let year = endYear; year >= startYear; year--) {
            years.push(year);
        }
        return years;
    }, [fallbackYear]);

    const yearOptions = React.useMemo<SelectProps['options']>(
        () => availableYears.map((year) => ({ label: String(year), value: year })),
        [availableYears]
    );

    const hasChanges = React.useMemo(() => {
        const hasNewRows = holidayRows.some((row) => row.status === 'new');
        return hasNewRows;
    }, [holidayRows]);

    const pricingYearEndDate = React.useMemo(() => {
        if (!selectedYear) {
            return '';
        }
        return dayjs(`${selectedYear}-12-31`).format('YYYY-MM-DD');
    }, [selectedYear]);

    const schoolSelectionKey = React.useMemo(() => {
        if (!selectedSchoolIds.length) {
            return '';
        }
        return normalizeSchoolSelection(selectedSchoolIds).sort().join('|');
    }, [selectedSchoolIds]);

    const handleSchoolSelectionChange = (ids: string[]) => {
        setSelectedSchoolIds(normalizeSchoolSelection(ids));
    };

    React.useEffect(() => {
        let cancelled = false;

        const loadReference = async () => {
            try {
                setIsLoadingReference(true);
                const [countriesData, centresData, schoolsData] = await Promise.all([
                    getAllCountries(),
                    getAllCentres(),
                    getAllSchools(),
                ]);
                if (cancelled) {
                    return;
                }
                setCountries(sortByName(countriesData ?? []));
                setCentres(sortByName(centresData ?? []));
                setSchools(sortByName(schoolsData ?? []));
                setErrorMessage(null);
            } catch (error) {
                if (!cancelled) {
                    const messageText = (error as Error)?.message ?? 'Failed to load reference data.';
                    setErrorMessage(messageText);
                    setCountries([]);
                    setCentres([]);
                    setSchools([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingReference(false);
                }
            }
        };

        loadReference();
        return () => {
            cancelled = true;
        };
    }, []);

    React.useEffect(() => {
        if (selectedYear != null && availableYears.includes(selectedYear)) {
            return;
        }
        setSelectedYear(availableYears.includes(fallbackYear) ? fallbackYear : availableYears[0]);
    }, [availableYears, selectedYear, fallbackYear]);

    const mapToRow = React.useCallback((record: HolidayRecord, index: number): HolidayRow => {
        const recordId = record.id;
        let uniqueId: string;
        if (recordId !== undefined && recordId !== null && recordId !== '') {
            uniqueId = String(recordId);
        } else {
            uniqueIdCounterRef.current += 1;
            uniqueId = `missing-${index}-${uniqueIdCounterRef.current}-${Math.random().toString(36).slice(2, 9)}`;
        }
        return {
            clientId: `existing-${uniqueId}`,
            status: 'existing',
            id: record.id,
            name: record.name,
            dateFrom: record.dateFrom ?? '',
            dateTo: record.dateTo ?? '',
            schoolIds: normalizeSchoolSelection(record.schoolIds),
            validationErrors: undefined,
        };
    }, []);

    const loadHolidays = React.useCallback(
        async (schoolIds: string[], year?: number) => {
            const normalized = normalizeSchoolSelection(schoolIds);
            if (!year || !normalized.length) {
                setHolidayRows([]);
                return;
            }

            const token = ++loadTokenRef.current;
            setIsLoadingHolidays(true);
            setErrorMessage(null);
            try {
                const response = await getHolidaysByPricingYear({ year, schoolIds: normalized });
                if (loadTokenRef.current !== token) {
                    return;
                }
                uniqueIdCounterRef.current = 0;
                setHolidayRows(response.map((item, index) => mapToRow(item, index)));
                setErrorMessage(null);
            } catch (error) {
                if (loadTokenRef.current !== token) {
                    return;
                }
                const messageText = (error as Error)?.message ?? 'Failed to load holidays.';
                setErrorMessage(messageText);
                setHolidayRows([]);
            } finally {
                if (loadTokenRef.current === token) {
                    setIsLoadingHolidays(false);
                }
            }
        },
        [mapToRow]
    );

    const refreshFromSignalR = React.useCallback(async () => {
        // Avoid overwriting in-progress edits/operations.
        if (hasChanges || saving || copying || deleting) return;
        await loadHolidays(selectedSchoolIds, selectedYear);
    }, [hasChanges, saving, copying, deleting, loadHolidays, selectedSchoolIds, selectedYear]);

    const { status, lastEventReceived } = useSignalREvent(
        [PublishedEventType.SchoolClosureEventPublished],
        refreshFromSignalR
    );

    React.useEffect(() => {
        if (isLoadingReference) {
            return;
        }
        loadHolidays(selectedSchoolIds, selectedYear);
    }, [loadHolidays, schoolSelectionKey, selectedYear, isLoadingReference]);

    const handleBulkAdd = () => {
        if (!selectedYear) {
            message.warning('Select a year before adding holidays.');
            return;
        }
        if (!selectedSchoolIds.length) {
            message.warning('Select at least one school to add a holiday.');
            return;
        }
        const clientId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const newRow: HolidayRow = {
            clientId,
            id: '',
            status: 'new',
            name: '',
            dateFrom: '',
            dateTo: '',
            schoolIds: [...selectedSchoolIds],
            validationErrors: undefined,
        };
        setHolidayRows((prev) => [...prev, newRow]);
        setFocusRowClientId(clientId);
    };

    const handleRowChange = (clientId: string, updates: Partial<HolidayRow>) => {
        setHolidayRows((prev) =>
            prev.map((row) => {
                if (row.clientId !== clientId) {
                    return row;
                }
                const validation = row.validationErrors ? { ...row.validationErrors } : undefined;
                if (validation) {
                    if (updates.name !== undefined) {
                        delete validation.name;
                    }
                    if (updates.dateFrom !== undefined) {
                        delete validation.dateFrom;
                    }
                    if (updates.dateTo !== undefined) {
                        delete validation.dateTo;
                    }
                    if (validation.general && !Object.keys(validation).filter((key) => key !== 'general').length) {
                        delete validation.general;
                    }
                }
                const updatedValidation = validation && Object.keys(validation).length ? validation : undefined;
                return {
                    ...row,
                    ...updates,
                    validationErrors: updatedValidation,
                };
            })
        );
    };

    const handleDeleteRow = React.useCallback(async (row: HolidayRow) => {
        if (row.status === 'new') {
            setHolidayRows((prev) => {
                const targetClientId = row.clientId;
                if (!targetClientId) {
                    console.error('Attempted to delete row without clientId');
                    return prev;
                }
                return prev.filter((item) => item.clientId !== targetClientId);
            });
            return;
        }

        if (!selectedYear) {
            message.warning('Select a year before deleting.');
            return;
        }

        if (!row.name || !row.dateFrom || !row.dateTo || !row.schoolIds || row.schoolIds.length === 0) {
            message.error('Unable to delete holiday: missing required information.');
            return;
        }

        setDeleting(true);
        setErrorMessage(null);

        try {
            const holidayToDelete = {
                name: row.name,
                dateFrom: row.dateFrom,
                dateTo: row.dateTo,
                schoolIds: normalizeSchoolSelection(row.schoolIds),
            };

            const response = await deleteHolidays({
                year: selectedYear,
                holidays: [holidayToDelete],
            });

            if (!response.ok) {
                if (response.errors && response.errors.length > 0) {
                    const errorText = response.errors.join('; ');
                    setErrorMessage(errorText);
                    message.error(errorText);
                } else {
                    const messageText = response.message ?? 'Failed to delete holiday.';
                    setErrorMessage(messageText);
                    message.error(messageText);
                }
                return;
            }

            message.success(response.message ?? 'Holiday deleted successfully.');
            setErrorMessage(null);
            
            await loadHolidays(selectedSchoolIds, selectedYear);
        } catch (error) {
            const messageText = (error as Error)?.message ?? 'Failed to delete holiday.';
            setErrorMessage(messageText);
            message.error(messageText);
        } finally {
            setDeleting(false);
        }
    }, [selectedYear, selectedSchoolIds, loadHolidays]);

    const validateDraftRows = (rows: HolidayRow[]): Record<string, HolidayRowValidation> => {
        const errors: Record<string, HolidayRowValidation> = {};
        rows.forEach((row) => {
            const validation: HolidayRowValidation = {};
            if (!row.name || !row.name.trim()) {
                validation.name = 'Holiday name is required.';
            }
            if (!row.dateFrom) {
                validation.dateFrom = 'Start date is required.';
            }
            if (!row.dateTo) {
                validation.dateTo = 'End date is required.';
            }
            if (row.dateFrom && row.dateTo && dayjs(row.dateFrom).isAfter(dayjs(row.dateTo))) {
                validation.dateTo = 'End date must be on or after the start date.';
            }
            if (Object.keys(validation).length) {
                errors[row.clientId] = validation;
            }
        });
        return errors;
    };

    const applyValidationErrors = (validationErrors: Record<string, HolidayRowValidation>) => {
        setHolidayRows((prev) =>
            prev.map((row) => ({
                ...row,
                validationErrors: validationErrors[row.clientId] ?? row.validationErrors,
            }))
        );
    };

    const DUPLICATE_MESSAGE = 'This holiday name already exists for the selected school(s). Use a unique name.';
    const computeDuplicateErrorsForNewRows = React.useCallback((rows: HolidayRow[]): Record<string, HolidayRowValidation> => {
        const keyToClientIds = new Map<string, string[]>();
        rows.forEach((row) => {
            const name = (row.name ?? '').trim();
            if (!name) return;
            normalizeSchoolSelection(row.schoolIds).forEach((schoolId) => {
                const key = `${schoolId}|${name.toLowerCase()}`;
                const list = keyToClientIds.get(key) ?? [];
                list.push(row.clientId);
                keyToClientIds.set(key, list);
            });
        });
        const errors: Record<string, HolidayRowValidation> = {};
        keyToClientIds.forEach((clientIds) => {
            if (clientIds.length <= 1) return;
            clientIds.forEach((clientId) => {
                const row = rows.find((r) => r.clientId === clientId);
                if (row?.status === 'new') {
                    errors[clientId] = { name: DUPLICATE_MESSAGE };
                }
            });
        });
        return errors;
    }, []);

    const duplicateErrors = React.useMemo(
        () => computeDuplicateErrorsForNewRows(holidayRows),
        [holidayRows, computeDuplicateErrorsForNewRows]
    );

    const hasDuplicateHolidays = React.useMemo(
        () => Object.keys(duplicateErrors).length > 0,
        [duplicateErrors]
    );

    const rowsWithValidation = React.useMemo(() => {
        return holidayRows.map((row) => {
            const dup = duplicateErrors[row.clientId];
            if (!dup) return row;
            return {
                ...row,
                validationErrors: { ...row.validationErrors, ...dup },
            };
        });
    }, [holidayRows, duplicateErrors]);

    const handleSave = async () => {
        if (!selectedYear) {
            message.warning('Select a year before saving.');
            return;
        }
        if (!selectedSchoolIds.length) {
            message.warning('Select at least one school to save holidays.');
            return;
        }
        const draftRows = holidayRows.filter((row) => row.status === 'new');
        if (!draftRows.length) {
            message.info('There are no changes to save.');
            return;
        }

        const validationErrors = validateDraftRows(draftRows);
        if (Object.keys(validationErrors).length) {
            applyValidationErrors(validationErrors);
            message.error('Please fix validation errors before saving.');
            return;
        }
        if (hasDuplicateHolidays) {
            message.error('Use a unique holiday name for each school before saving.');
            return;
        }

        setSaving(true);
        try {
            const newHolidays = draftRows.flatMap((row) =>
                normalizeSchoolSelection(row.schoolIds).map((schoolId) => ({
                    name: row.name.trim(),
                    dateFrom: row.dateFrom,
                    dateTo: row.dateTo,
                    schoolIds: [schoolId],
                }))
            );

            if (newHolidays.length === 0) {
                message.info('There are no changes to save.');
                setSaving(false);
                return;
            }

            const response = await createHolidays({
                year: selectedYear,
                holidays: newHolidays,
            });
            
            if (!response.ok) {
                if (response.errors && response.errors.length > 0) {
                    const errorText = response.errors.join('; ');
                    setErrorMessage(errorText);
                    message.error(errorText);
                } else {
                    const messageText = response.message ?? 'Failed to save holidays.';
                    setErrorMessage(messageText);
                    message.error(messageText);
                }
                return;
            }

            message.success('Holidays saved successfully.');
            setErrorMessage(null);
            await loadHolidays(selectedSchoolIds, selectedYear);
        } catch (error) {
            const messageText = (error as Error)?.message ?? 'Failed to save holidays.';
            setErrorMessage(messageText);
            message.error(messageText);
        } finally {
            setSaving(false);
        }
    };

    const recalculateDateForNewYear = React.useCallback((dateStr: string, sourceYear: number, targetYear: number): string => {
        if (!dateStr) return '';
        const date = dayjs(dateStr);
        const yearDiff = targetYear - sourceYear;
        return date.add(yearDiff, 'year').format('YYYY-MM-DD');
    }, []);

    const executeCopy = React.useCallback(async () => {
        if (!selectedYear || !selectedSchoolIds.length) {
            return;
        }

        const previousYear = selectedYear - 1;
        setCopying(true);
        setErrorMessage(null);
        try {
            const previousHolidays = await getHolidaysByPricingYear({
                year: previousYear,
                schoolIds: selectedSchoolIds,
            });

            if (previousHolidays.length === 0) {
                message.info('No holidays found in the previous year.');
                setCopying(false);
                return;
            }

            const selectedSet = new Set(normalizeSchoolSelection(selectedSchoolIds));
            const copiedRows: HolidayRow[] = [];
            previousHolidays.forEach((holiday, index) => {
                const schoolsForHoliday = normalizeSchoolSelection(holiday.schoolIds).filter((sid) =>
                    selectedSet.has(sid)
                );
                schoolsForHoliday.forEach((schoolId) => {
                    const newDateFrom = recalculateDateForNewYear(holiday.dateFrom, previousYear, selectedYear);
                    const newDateTo = recalculateDateForNewYear(holiday.dateTo, previousYear, selectedYear);
                    uniqueIdCounterRef.current += 1;
                    const clientId = `copied-${Date.now()}-${index}-${uniqueIdCounterRef.current}-${schoolId}`;
                    copiedRows.push({
                        clientId,
                        status: 'new' as const,
                        id: '',
                        name: holiday.name,
                        dateFrom: newDateFrom,
                        dateTo: newDateTo,
                        schoolIds: [schoolId],
                        validationErrors: undefined,
                    });
                });
            });

            if (copiedRows.length === 0) {
                message.info('No holidays found in the previous year for the selected schools.');
                setCopying(false);
                return;
            }

            const existingKeys = new Set<string>();
            holidayRows.forEach((row) => {
                if (!row.dateFrom || !row.dateTo) return;
                normalizeSchoolSelection(row.schoolIds).forEach((schoolId) => {
                    existingKeys.add(`${schoolId}|${row.dateFrom}|${row.dateTo}`);
                });
            });
            const toAdd = copiedRows.filter((row) => {
                const schoolId = row.schoolIds[0];
                if (!schoolId) return true;
                const key = `${schoolId}|${row.dateFrom}|${row.dateTo}`;
                return !existingKeys.has(key);
            });
            const skippedCount = copiedRows.length - toAdd.length;
            if (toAdd.length === 0) {
                message.info(
                    skippedCount > 0
                        ? 'All copied holidays already exist for this year.'
                        : 'No holidays found in the previous year for the selected schools.'
                );
                setCopying(false);
                return;
            }

            setHolidayRows((prev) => [...prev, ...toAdd]);
            setFocusRowClientId(toAdd[0]?.clientId ?? null);

            message.success(
                skippedCount > 0
                    ? `Copied ${toAdd.length} holiday(s); ${skippedCount} already existed and were skipped.`
                    : `Copied ${toAdd.length} holiday(s) from previous year.`
            );
        } catch (error) {
            const messageText = (error as Error)?.message ?? 'Failed to copy holidays from previous year.';
            setErrorMessage(messageText);
            message.error(messageText);
        } finally {
            setCopying(false);
        }
    }, [selectedYear, selectedSchoolIds, recalculateDateForNewYear, holidayRows]);

    const handleCopy = () => {
        if (!selectedYear) {
            message.warning('Select a year before copying.');
            return;
        }
        if (!selectedSchoolIds.length) {
            message.warning('Select at least one school to copy holidays.');
            return;
        }
        executeCopy();
    };

    const disableBulkAdd =
        !selectedYear ||
        !selectedSchoolIds.length ||
        isLoadingHolidays ||
        saving ||
        copying ||
        deleting;
    const disableCopy =
        !selectedYear ||
        !selectedSchoolIds.length ||
        isLoadingHolidays ||
        saving ||
        copying ||
        deleting ||
        hasChanges;
    const disableSave = saving || isLoadingHolidays || deleting || hasDuplicateHolidays;

    const renderContent = () => {
        if (isLoadingHolidays) {
            return (
                <div className="holiday-manager__loading">
                    <Spin />
                </div>
            );
        }
        if (!selectedSchoolIds.length) {
            return (
                <Empty description="Select schools to view holidays" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            );
        }
        if (!holidayRows.length) {
            return (
                <Empty description="No holidays to display" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            );
        }
        return (
            <HolidayTable
                rows={rowsWithValidation}
                schoolNameMap={schoolNameMap}
                onRowChange={handleRowChange}
                onDelete={handleDeleteRow}
                pricingYearEndDate={pricingYearEndDate}
                disabled={saving || copying || deleting}
                focusRowClientId={focusRowClientId}
                onFocusDone={() => setFocusRowClientId(null)}
            />
        );
    };

    return (
        <div className="holiday-manager">
            <div className="holiday-manager__header">
                    <Title level={2} style={{ marginBottom: 0 }}>
                        Holiday Management
                    </Title>
                    <StatusIcon
                        status={status}
                        lastEventReceived={lastEventReceived}
                        unpublishedChanges={hasChanges || saving || copying || deleting}
                        onManualRefresh={() => refreshFromSignalR()}
                    />
                </div>
            <div className="holiday-manager__layout">
                <div className="holiday-manager__sidebar">
                    <div className="holiday-manager__year-select">
                        <label htmlFor="holiday-year-select">
                            <Text strong>Select Year</Text>
                        </label>
                        <Select
                            id="holiday-year-select"
                            placeholder="Select a year"
                            value={selectedYear}
                            onChange={(value: number) => setSelectedYear(value)}
                            options={yearOptions}
                            disabled={isLoadingReference}
                            style={{ width: '100%', marginTop: 8 }}
                        />
                    </div>
                    <SchoolSelector
                        countries={countries}
                        centres={centres}
                        schools={schools}
                        selectedSchoolIds={selectedSchoolIds}
                        onSelectionChange={handleSchoolSelectionChange}
                        loading={isLoadingReference}
                        disabled={saving || copying || deleting}
                    />
                </div>
                <div className="holiday-manager__content">
                    {errorMessage ? (
                        <Alert
                            type="error"
                            title="Unable to process request"
                            description={errorMessage}
                            banner
                            closable
                            onClose={() => setErrorMessage(null)}
                            style={{ marginBottom: 16 }}
                        />
                    ) : null}
                    <HolidayToolbar
                        onCopy={handleCopy}
                        onSave={handleSave}
                        onBulkAdd={handleBulkAdd}
                        disableCopy={disableCopy}
                        disableSave={disableSave}
                        disableBulkAdd={disableBulkAdd}
                        saving={saving}
                        copying={copying}
                        hasChanges={hasChanges}
                        hasDuplicateHolidays={hasDuplicateHolidays}
                    />
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default HolidayManagerPage;
